import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "../shared/Sidebar";

function DocumentsUpload() {
  
 const baseUrl = process.env.baseUrl;
  const navigate = useNavigate();

  // State to hold files for each input
  const [files, setFiles] = useState({
    tradeLicense: null,
    plantLayout: null,
    bills: [],
    ghgReport: null,
    wwtpLayout: null,
    waterBalance: null,
    massBalance: null,
    energyBalance: null,
    manufacturingDesc: null,
  });

  // State for form errors
  const [errors, setErrors] = useState({});

  // Handle file input changes
  const handleFileChange = (e, field, multiple = false) => {
    const selectedFiles = multiple ? Array.from(e.target.files) : e.target.files[0];
    setFiles((prev) => ({ ...prev, [field]: selectedFiles }));
  };

  // Validation function
  const validate = () => {
    const newErrors = {};

    // Required files validation
    if (!files.tradeLicense) newErrors.tradeLicense = "Trade/Industrial License is required.";
    if (!files.plantLayout) newErrors.plantLayout = "Plant Layout is required.";
    if (!files.bills || files.bills.length === 0) newErrors.bills = "Water & Electricity Bills are required.";
    if (!files.ghgReport) newErrors.ghgReport = "GHG Inventory Report is required.";
    if (!files.manufacturingDesc) newErrors.manufacturingDesc = "Manufacturing Process Description is required.";

    // File type checks (optional, since inputs have accept attribute)
    if (files.tradeLicense && files.tradeLicense.type !== "application/pdf") {
      newErrors.tradeLicense = "Trade License must be a PDF file.";
    }

    if (files.plantLayout) {
      const allowedPlantTypes = ["application/pdf", "application/acad", "application/dwg", "image/vnd.dwg"];
      // Note: .dwg mime types vary, browsers might not detect accurately, so be lenient
      if (
        files.plantLayout.type !== "application/pdf" &&
        !files.plantLayout.name.toLowerCase().endsWith(".dwg")
      ) {
        newErrors.plantLayout = "Plant Layout must be .dwg or .pdf file.";
      }
    }

    // You can add more file type validations as needed

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    // Prepare form data
    const formData = new FormData();
    formData.append("tradeLicense", files.tradeLicense);
    formData.append("plantLayout", files.plantLayout);
    files.bills.forEach((file, idx) => formData.append(`bills[${idx}]`, file));
    formData.append("ghgReport", files.ghgReport);
    if (files.wwtpLayout) formData.append("wwtpLayout", files.wwtpLayout);
    if (files.waterBalance) formData.append("waterBalance", files.waterBalance);
    if (files.massBalance) formData.append("massBalance", files.massBalance);
    if (files.energyBalance) formData.append("energyBalance", files.energyBalance);
    formData.append("manufacturingDesc", files.manufacturingDesc);

    try {
      const response = await axios.post(
        "/api/upload-documents",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      // On success, redirect or show message
      alert("Documents uploaded successfully!");
      navigate("/operationaldata");
    } catch (error) {
      console.error(error);
      alert("Upload failed. Please try again.");
    }
  };

  return (
    <div className="container col-lg-9 mt-5">
      <div className="row">
        <div className="col-md-4">
          <Sidebar />
        </div>

        <div className="col-md-8">
          <div className="form-section">
            <h5 className="mb-4">Mandatory Documents Upload</h5>
            <form onSubmit={handleSubmit} noValidate>
              <div className="row mb-3">
                <div className="col-md-12 mb-3">
                  <label className="form-label">
                    Trade/Industrial License <small>(.pdf)</small>
                  </label>
                  <input
                    type="file"
                    className={`form-control ${errors.tradeLicense ? "is-invalid" : ""}`}
                    accept=".pdf"
                    onChange={(e) => handleFileChange(e, "tradeLicense")}
                    required
                  />
                  {errors.tradeLicense && (
                    <div className="invalid-feedback">{errors.tradeLicense}</div>
                  )}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Plant Layout <small>(.dwg / .pdf)</small>
                  </label>
                  <input
                    type="file"
                    className={`form-control ${errors.plantLayout ? "is-invalid" : ""}`}
                    accept=".dwg,.pdf"
                    onChange={(e) => handleFileChange(e, "plantLayout")}
                    required
                  />
                  {errors.plantLayout && (
                    <div className="invalid-feedback">{errors.plantLayout}</div>
                  )}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Water & Electricity Bills (ADDC) <small>(Past 12 months)</small>
                  </label>
                  <input
                    type="file"
                    className={`form-control ${errors.bills ? "is-invalid" : ""}`}
                    multiple
                    onChange={(e) => handleFileChange(e, "bills", true)}
                    required
                  />
                  {errors.bills && <div className="invalid-feedback">{errors.bills}</div>}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    GHG Inventory Report <small>(Scope 1/2 or 1/2/3)</small>
                  </label>
                  <input
                    type="file"
                    className={`form-control ${errors.ghgReport ? "is-invalid" : ""}`}
                    onChange={(e) => handleFileChange(e, "ghgReport")}
                    required
                  />
                  {errors.ghgReport && (
                    <div className="invalid-feedback">{errors.ghgReport}</div>
                  )}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    WWTP Layout + Effluent <small>(Optional if not present)</small>
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    onChange={(e) => handleFileChange(e, "wwtpLayout")}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Water Balance Diagram <small>(Optional)</small>
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    onChange={(e) => handleFileChange(e, "waterBalance")}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Mass Balance Diagram <small>(Optional)</small>
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    onChange={(e) => handleFileChange(e, "massBalance")}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Energy Balance Diagram <small>(Optional)</small>
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    onChange={(e) => handleFileChange(e, "energyBalance")}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Manufacturing Process Description <small>(PDF)</small>
                  </label>
                  <input
                    type="file"
                    className={`form-control ${errors.manufacturingDesc ? "is-invalid" : ""}`}
                    accept=".pdf,.txt,.doc,.docx"
                    onChange={(e) => handleFileChange(e, "manufacturingDesc")}
                    required
                  />
                  {errors.manufacturingDesc && (
                    <div className="invalid-feedback">{errors.manufacturingDesc}</div>
                  )}
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-custom w-100"
              >
                Submit
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DocumentsUpload;
